import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react'
import { useToast } from '../../platform/feedback/ToastProvider'
import type { Selection } from '../../platform/primitives/DataGrid'
import type { Txn } from './transactionWire'
import { rowSummary } from './txnDisplay'

/** One bulk operation touches at most this many rows (Q4). */
export const BULK_CAP = 100

type SelectionState = { byId: ReadonlyMap<string, Txn>; identity: string; generation: number }

export type SelectionAction =
  | { t: 'toggle'; row: Txn }
  | { t: 'setPage'; rows: Txn[]; on: boolean }
  | { t: 'clear' }
  | { t: 'filtersChanged'; identity: string }
  /** Refresh the snapshots of rows STILL selected from rows freshly read. It never adds a row. */
  | { t: 'replace'; rows: Txn[] }
  /** After a bulk operation: only these ids stay selected (the failures). */
  | { t: 'retain'; ids: readonly string[] }

function reducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.t) {
    case 'toggle': {
      const byId = new Map(state.byId)
      if (byId.has(action.row.id)) byId.delete(action.row.id)
      else byId.set(action.row.id, action.row)
      return { ...state, byId }
    }
    case 'setPage': {
      const byId = new Map(state.byId)
      for (const r of action.rows) {
        if (action.on) byId.set(r.id, r)
        else byId.delete(r.id)
      }
      return { ...state, byId }
    }
    case 'clear':
      return { ...state, byId: new Map() }
    case 'filtersChanged':
      return action.identity === state.identity
        ? state
        : { identity: action.identity, byId: new Map(), generation: state.generation + 1 }
    case 'replace': {
      // Only what is still selected. A late result from an operation that
      // started under filters the user has since left must not bring a row back.
      if (!action.rows.some((r) => state.byId.has(r.id))) return state
      const byId = new Map(state.byId)
      for (const r of action.rows) if (byId.has(r.id)) byId.set(r.id, r)
      return { ...state, byId }
    }
    case 'retain': {
      const keep = new Set(action.ids)
      return { ...state, byId: new Map([...state.byId].filter(([id]) => keep.has(id))) }
    }
  }
}

type Api = {
  /**
   * Counts every change of filters, never repeats. A bulk operation, or an open
   * dialog, compares it against the one it started under. Not the identity
   * string: filters A→B→A (history Back, then Forward) end where they began, and
   * the selection was cleared in between.
   */
  generation: number
  selected: Txn[]
  count: number
  isSelected: (id: string) => boolean
  dispatch: (a: SelectionAction) => void
}

const SelectionContext = createContext<Api | null>(null)
const NONE: ReadonlyMap<string, Txn> = new Map()

/**
 * The selection: a map of row SNAPSHOTS, so it survives paging (Q4) — page 1's
 * rows are not in memory once page 2 is on screen, and a bulk action needs their
 * tags and merchants.
 *
 * It is tied to `identity`, the filters minus the page. When the identity
 * changes under a non-empty selection the selection is cleared and the user is
 * told (M6-R8); a silent clear reads as a lost click. The clear is **derived
 * during render** — the stale map is simply not exposed while the identities
 * disagree — so no frame paints the old selection against the new rows, and the
 * reducer catches up in an effect, which is also where the toast belongs.
 *
 * `onStarted` fires on each 0 → n transition, once. The list points the
 * analytics event at it.
 */
export function SelectionProvider({
  identity,
  onStarted,
  children,
}: {
  identity: string
  onStarted?: () => void
  children: ReactNode
}) {
  const [state, dispatch] = useReducer(reducer, { byId: NONE, identity, generation: 0 })
  const toast = useToast()
  const behind = state.identity !== identity
  const byId = behind ? NONE : state.byId

  useEffect(() => {
    if (state.identity === identity) return
    if (state.byId.size > 0) toast.show({ kind: 'info', message: 'Selection cleared — filters changed' })
    dispatch({ t: 'filtersChanged', identity })
  }, [identity, state.identity, state.byId, toast])

  const previous = useRef(0)
  const started = useRef(onStarted)
  useEffect(() => {
    started.current = onStarted
  })
  useEffect(() => {
    if (previous.current === 0 && byId.size > 0) started.current?.()
    previous.current = byId.size
  }, [byId])

  // While the reducer is a render behind, the generation it is about to reach.
  const generation = state.generation + (behind ? 1 : 0)
  const api = useMemo<Api>(
    () => ({
      generation,
      selected: [...byId.values()],
      count: byId.size,
      isSelected: (id) => byId.has(id),
      dispatch,
    }),
    [byId, generation],
  )
  return <SelectionContext.Provider value={api}>{children}</SelectionContext.Provider>
}

export function useSelection(): Api {
  const api = useContext(SelectionContext)
  if (api === null) throw new Error('useSelection must be used inside a <SelectionProvider>')
  return api
}

/**
 * The grid's `Selection` over the rows on screen. The header box acts on the
 * VISIBLE rows only: "all" is the page, never the rows other pages hold.
 */
export function usePageSelection(rows: readonly Txn[]): Selection<Txn> {
  const { isSelected, dispatch } = useSelection()
  const onToggle = useCallback((row: Txn) => dispatch({ t: 'toggle', row }), [dispatch])
  const visible = rows.filter((r) => isSelected(r.id)).length
  const allVisible: boolean | 'mixed' = rows.length > 0 && visible === rows.length ? true : visible > 0 ? 'mixed' : false
  return {
    isSelected: (row) => isSelected(row.id),
    onToggle,
    allVisible,
    onToggleAllVisible: () => dispatch({ t: 'setPage', rows: [...rows], on: allVisible !== true }),
    rowLabel: (row) => `Select ${rowSummary(row)}`,
  }
}
